/**
 * Ghana Card Verification Service
 * Task 2.4: Implement Ghana Card Verification Frontend
 * 
 * User Story: "As a claimant, I must provide my passport photo and Ghana Card number for identity verification 
 * before submitting a claim, with clear feedback if verification fails."
 * 
 * This service handles identity verification by sending passport photo and Ghana Card number to the backend
 * following the authentication patterns established in Task 2.3.
 */

import { apiClient } from '../../utils/apiClient';
import { processVerificationError, getErrorMessage } from '../../constants/verificationErrors'; // Phase 2: Enhanced Features
import { PENDING_VERIFICATION, VERIFIED, VERIFICATION_FAILED } from '../../constants/verificationStatus';

/**
 * Upload passport photo and Ghana Card number for identity verification
 * @param {File} passportPhoto - The passport photo file
 * @param {string} ghanaCardNumber - The Ghana Card number
 * @returns {Promise<{verificationId: string}>} The verification ID for status polling
 */
export const uploadIdentityVerification = async (passportPhoto, ghanaCardNumber) => {
  console.log('🔧 verificationService: uploadIdentityVerification called', {
    hasPassportPhoto: !!passportPhoto,
    photoName: passportPhoto?.name,
    photoSize: passportPhoto?.size,
    photoType: passportPhoto?.type,
    ghanaCardNumber
  });

  try {
    // Create FormData matching backend expectations
    const formData = new FormData();
    formData.append('image', passportPhoto); // Backend expects 'image' field
    formData.append('ghanaCardNumber', ghanaCardNumber); // Backend expects 'ghanaCardNumber' field
    
    console.log('📤 verificationService: Sending POST request to verification endpoint', {
      endpoint: 'verification',
      formDataKeys: Array.from(formData.keys()),
      imageField: formData.get('image')?.name,
      ghanaCardField: formData.get('ghanaCardNumber')
    });

    // Use apiClient.post for consistent authentication and error handling
    const response = await apiClient.post('verification', formData);
    
    console.log('📥 verificationService: Received response', {
      status: response.status,
      data: response.data,
      responseType: typeof response.data
    });

    // Handle standardized response format: { data: { verificationId } }
    const responseData = response.data.data || response.data;
    
    console.log('🔍 verificationService: Processing response data', {
      responseData,
      hasVerificationId: !!responseData.verificationId,
      verificationId: responseData.verificationId
    });

    // Phase 1: Critical Fix - Verify verificationId exists before returning
    if (!responseData.verificationId) {
      console.log('❌ verificationService: No verification ID in response');
      throw new Error('No verification ID received from server');
    }
    
    // PHASE 2: Include validation data in response
    const result = {
      verificationId: responseData.verificationId,
      status: responseData.verificationStatus || PENDING_VERIFICATION,
      validationData: responseData.validationData || null
    };
    
    console.log('✅ verificationService: Returning successful result', result);
    return result;
  } catch (error) {
    console.log('❌ verificationService: Upload failed', {
      error: error.message,
      errorCode: error.code,
      errorStatus: error.status,
      errorResponse: error.response?.data,
      originalError: error
    });

    // Phase 2: Enhanced Features - Handle already processed errors from apiClient
    let processedError;
    
    // Check if error is already processed by handleApiError (has code, message, details structure)
    if (error.code && error.message && typeof error.details !== 'undefined') {
      processedError = {
        code: error.code,
        message: error.message,
        details: error.details,
        niaCode: error.niaCode || null,
        status: error.httpStatus || error.status || 500
      };
    } else {
      // Fallback to processVerificationError for unprocessed errors
      processedError = processVerificationError(error, error.response?.status);
    }
    
    // Create a structured error object for the UI with all details
    const enhancedError = new Error(processedError.message);
    enhancedError.code = processedError.code;
    enhancedError.reason = processedError.message;
    enhancedError.details = processedError.details; // ✅ Include details from backend
    enhancedError.niaCode = processedError.niaCode; // ✅ Include NIA code
    enhancedError.originalError = error.message;
    enhancedError.status = processedError.status;
    
    throw enhancedError;
  }
};

/**
 * Poll verification status for a given verification ID
 * @param {string} verificationId - The verification ID to check
 * @returns {Promise<{status: string, details?: object}>} Current verification status
 */
export const getVerificationStatus = async (verificationId) => {
  console.log('🔍 verificationService: getVerificationStatus called', { verificationId });
  
  try {
    if (!verificationId) {
      throw new Error('Verification ID is required');
    }
    
    console.log('📡 verificationService: Making GET request to status endpoint');
    // Phase 1: Critical Fix - Correct API endpoint URL
    const response = await apiClient.get(`verification/${verificationId}/status`);
    
    console.log('📥 verificationService: Status response received', {
      status: response.status,
      data: response.data,
      dataType: typeof response.data
    });

    // Handle standardized response format
    const responseData = response.data.data || response.data;

    console.log('🔍 verificationService: Processing status response', {
      responseData,
      verificationStatus: responseData.verificationStatus,
      rawStatus: responseData.status
    });

    const result = {
      status: responseData.verificationStatus || PENDING_VERIFICATION,
      details: responseData.details || {},
      verificationId: responseData.verificationId || verificationId,
      // Include any additional verification data
      ghanaCardNumber: responseData.ghanaCardNumber,
      verifiedAt: responseData.verifiedAt,
      failureReason: responseData.failureReason,
      failureCode: responseData.failureCode || responseData.errorCode,
      niaResponseCode: responseData.niaResponseCode,
      // PHASE 2: Include validation data for claims form validation
      validationData: responseData.validationData || null
    };

    console.log('✅ verificationService: Returning status result', result);
    return result;
  } catch (error) {
    console.log('❌ verificationService: getVerificationStatus failed', {
      error: error.message,
      errorCode: error.code,
      errorStatus: error.status,
      verificationId
    });
    // Phase 2: Enhanced Features - Use structured error processing
    const processedError = processVerificationError(error, error.response?.status);
    console.error('Failed to fetch verification status:', processedError);
    throw new Error(processedError.message);
  }
};

/**
 * Poll verification status with automatic retry until completion
 * @param {string} verificationId - The verification ID to poll
 * @param {function} onStatusUpdate - Callback called on each status update
 * @param {number} maxAttempts - Maximum polling attempts (default: 30)
 * @param {number} intervalMs - Polling interval in milliseconds (default: 2000)
 * @returns {Promise<{status: string, details?: object}>} Final verification result
 */
export const pollVerificationStatus = async (
  verificationId,
  onStatusUpdate = () => {},
  maxAttempts = 30,
  intervalMs = 2000
) => {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    try {
      const statusResult = await getVerificationStatus(verificationId);
      
      // Call the status update callback
      onStatusUpdate(statusResult);
      
      // Check if verification is complete
      if (statusResult.status === VERIFIED || statusResult.status === VERIFICATION_FAILED) {
        return statusResult;
      }
      
      // If still pending, wait before next poll
      if (statusResult.status === PENDING_VERIFICATION) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;
      } else {
        // Unknown status, return as-is
        return statusResult;
      }
    } catch (error) {
      attempts++;
      console.warn(`Verification polling attempt ${attempts} failed:`, error.message);
      
      // If we've exhausted attempts, throw the error
      if (attempts >= maxAttempts) {
        throw new Error('Verification status polling failed. Please refresh and try again.');
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  // If we reach here, polling timed out
  throw new Error('Verification is taking longer than expected. Please contact support.');
};

/**
 * Validate that a file is a valid passport photo for identity verification
 * @param {File} file - The file to validate
 * @returns {boolean} True if file is valid
 * @throws {Error} If file is invalid
 */
export const validatePassportPhoto = (file) => {
  if (!file) {
    throw new Error('Please select a passport photo to upload.');
  }
  
  // Check file type - support PNG, JPEG, JPG, and WebP
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Please upload a valid image file (PNG, JPEG, JPG, or WebP).');
  }
  
  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    throw new Error('Image file size must be less than 5MB.');
  }
  
  // Check minimum file size (avoid empty files)
  const minSize = 1024; // 1KB minimum
  if (file.size < minSize) {
    throw new Error('Image file appears to be empty or corrupted.');
  }
  
  return true;
};

export default {
  uploadIdentityVerification,
  getVerificationStatus,
  pollVerificationStatus,
  validatePassportPhoto
};
